import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchProjectByIdApi, updateProjectApi } from "../../services/api/boq/projectService";
import { fetchClientsApi } from "../../services/api/boq/clientService";
// import { fetchUsersApi } from "../../services/api/userService"; // for managers
import toast from "react-hot-toast";

export default function EditProject() {
    const { id } = useParams();
    const navigate = useNavigate();

    const projectTypes = ["Residential", "Commercial", "Infra"];
    const billingTypes = [
        { label: "BOQ", value: "BOQ" },
        { label: "Fixed", value: "fixed" },
        { label: "Milestone", value: "milestone" },
    ];
    const statuses = ["active", "inactive", "completed"];

    const [clients, setClients] = useState([]);
    const [managers, setManagers] = useState([]);
    const [clientSearch, setClientSearch] = useState("");
    const [showClientDropdown, setShowClientDropdown] = useState(false);
    const [loading, setLoading] = useState(true);
    const [errors, setErrors] = useState({});
    const dropdownRef = useRef(null);

    const [formData, setFormData] = useState({
        project_code: "",
        project_name: "",
        client_id: "",
        project_type: "",
        billing_type: "",
        start_date: "",
        end_date: "",
        project_value: "",
        approved_budget: "",
        status: "active",
        project_manager_id: "",
        description: "",
    });

    /* ------------------- LOAD DATA ------------------- */
    useEffect(() => {
        (async () => {
            await loadClients();
            await loadManagers();
            await loadProject();
        })();

        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowClientDropdown(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);


    const loadClients = async () => {
        try {
            const res = await fetchClientsApi();

            // res is Axios response
            console.log("CLIENT API DATA:", res.data);

            setClients(res.data || []);
        } catch (e) {
            console.log(e);
            toast.error("Failed to load clients");
        }
    };


    const loadManagers = async () => {
        setManagers([
            { id: 1, name: "Manager A" },
            { id: 2, name: "Manager B" },
        ]);
    };

    const formatDate = (dateStr) => (dateStr ? dateStr.slice(0, 10) : "");

    const loadProject = async () => {
        try {
            const res = await fetchProjectByIdApi(id);
            const p = res.data;

            setFormData({
                project_code: p.project_code,
                project_name: p.project_name,
                client_id: p.client_id,
                project_type: p.project_type,
                billing_type: p.billing_type,
                start_date: formatDate(p.start_date),
                end_date: formatDate(p.end_date),
                project_value: p.project_value,
                approved_budget: p.approved_budget,
                status: p.status || "active",
                project_manager_id: p.project_manager_id || "",
                description: p.description || "",
            });

        } catch {
            toast.error("Failed to load project");
        } finally {
            setLoading(false);
        }
    };

    // ✅ SET CLIENT NAME AFTER CLIENTS ARE LOADED
    useEffect(() => {
        if (!formData.client_id || clients.length === 0) return;

        const selectedClient = clients.find(
            c => Number(c.id) === Number(formData.client_id)
        );

        if (selectedClient) {
            setClientSearch(getClientName(selectedClient));
        }
    }, [clients, formData.client_id]);




    /* ------------------- VALIDATION ------------------- */
    const validateField = (name, value) => {
        let message = "";

        // Required fields
        const requiredFields = [
            "project_name",
            "client_id",
            "project_type",
            "billing_type",
            "start_date",
            "end_date",
            "project_value",
            "approved_budget",
        ];

        if (!value && requiredFields.includes(name)) {
            message = "Required";
        }

        // Special rule for end_date
        if (name === "end_date" && value && formData.start_date && value < formData.start_date) {
            message = "End date must be after start date";
        }

        setErrors(prev => ({ ...prev, [name]: message }));
    };


    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        validateField(name, value);
    };

    const validateForm = () => {
        const temp = {};
        ["project_name", "client_id", "project_type", "billing_type", "start_date", "end_date", "project_value", "approved_budget"].forEach((field) => {
            if (!formData[field]) temp[field] = "Required";
        });
        if (formData.end_date < formData.start_date) temp.end_date = "End date must be after start date";
        setErrors(temp);
        return Object.keys(temp).length === 0;
    };

    /* ------------------- UPDATE ------------------- */
    const handleUpdate = async () => {
        if (!formData.client_id) {
            toast.error("Please select a client from the list");
            return;
        }
        if (!validateForm()) return;
        try {
            const payload = {
                project_name: formData.project_name,
                client_id: Number(formData.client_id),
                project_type: formData.project_type,
                billing_type: formData.billing_type,
                start_date: formData.start_date,
                end_date: formData.end_date,
                project_value: Number(formData.project_value),
                approved_budget: Number(formData.approved_budget),
                status: formData.status,
                description: formData.description,
                project_manager_id: Number(formData.project_manager_id) || null,
            };

            await updateProjectApi(id, payload);
            toast.success("Project updated successfully");
            navigate("/projects", { state: { refresh: true } });
        } catch (err) {
            console.log(err.response || err);
            toast.error("Update failed");
        }
    };

    if (loading) return <p className="p-6">Loading project...</p>;

    const getClientName = (c) => c.name || c.client_name || "";

    /* ------------------- UI ------------------- */
    return (
        <div className="p-6 bg-gray-100 min-h-screen">
            <div className="bg-white p-6 rounded-xl max-w-3xl mx-auto shadow">
                <h2 className="text-xl font-semibold mb-6">Edit Project</h2>

                <div className="grid grid-cols-2 gap-4">
                    {/* Project Code */}
                    <div>
                        <label className="text-sm font-medium">Project Code</label>
                        <input value={formData.project_code} disabled className="border p-2 w-full rounded bg-gray-200" />
                    </div>

                    {/* Project Name */}
                    <div>
                        <label className="text-sm font-medium">Project Name *</label>
                        <input
                            name="project_name"
                            value={formData.project_name}
                            onChange={handleChange}
                            onBlur={(e) => validateField(e.target.name, e.target.value)}
                            className={`border p-2 w-full rounded ${errors.project_name ? "border-red-500" : ""}`}
                        />
                        {errors.project_name && <p className="text-red-500 text-xs">{errors.project_name}</p>}
                    </div>

                    {/* Client */}
                    <div className="col-span-2 relative" ref={dropdownRef}>
                        <label className="text-sm font-medium">Client *</label>
                        <input
                            value={clientSearch}
                            onChange={(e) => {
                                setClientSearch(e.target.value);
                                setFormData(prev => ({ ...prev, client_id: "" }));
                                setShowClientDropdown(true);
                            }}
                            onBlur={() => validateField("client_id", formData.client_id)}
                            onClick={(e) => { e.stopPropagation(); setShowClientDropdown(true); }}
                            onFocus={() => setShowClientDropdown(true)}
                            className={`border p-2 w-full rounded cursor-pointer ${errors.client_id ? "border-red-500" : ""}`}
                            placeholder="Select client"
                        />
                        {errors.client_id && <p className="text-red-500 text-xs">{errors.client_id}</p>}

                        {showClientDropdown && (
                            <div className="absolute bg-white border w-full max-h-60 overflow-y-auto z-[9999] rounded shadow-lg mt-1">
                                {clients.filter(c =>
                                    clientSearch ? getClientName(c).toLowerCase().includes(clientSearch.toLowerCase()) : true
                                ).length === 0 ? (
                                    <div className="px-4 py-2 text-gray-400">No clients found</div>
                                ) : (
                                    clients.filter(c =>
                                        clientSearch ? getClientName(c).toLowerCase().includes(clientSearch.toLowerCase()) : true
                                    ).map(c => (
                                        <div
                                            key={c.id}
                                            className="px-4 py-2 hover:bg-blue-50 cursor-pointer"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setFormData(prev => ({ ...prev, client_id: c.id }));
                                                setClientSearch(getClientName(c));
                                                setShowClientDropdown(false);
                                                validateField("client_id", c.id);
                                            }}
                                        >
                                            {getClientName(c)}
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                    </div>

                    {/* Project Type */}
                    <div>
                        <label className="text-sm font-medium">Project Type *</label>
                        <select
                            name="project_type"
                            value={formData.project_type}
                            onChange={handleChange}
                            onBlur={(e) => validateField(e.target.name, e.target.value)}
                            className={`border p-2 w-full rounded ${errors.project_type ? "border-red-500" : ""}`}
                        >
                            <option value="">Select Project Type</option>
                            {projectTypes.map(type => <option key={type} value={type}>{type}</option>)}
                        </select>
                        {errors.project_type && <p className="text-red-500 text-xs">{errors.project_type}</p>}
                    </div>

                    {/* Billing Type */}
                    <div>
                        <label className="text-sm font-medium">Billing Type *</label>
                        <select
                            name="billing_type"
                            value={formData.billing_type}
                            onChange={handleChange}
                            onBlur={(e) => validateField(e.target.name, e.target.value)}
                            className={`border p-2 w-full rounded ${errors.billing_type ? "border-red-500" : ""}`}
                        >
                            <option value="">Select Billing Type</option>
                            {billingTypes.map(type => <option key={type.value} value={type.value}>{type.label}</option>)}
                        </select>
                        {errors.billing_type && <p className="text-red-500 text-xs">{errors.billing_type}</p>}
                    </div>

                    {/* Start Date */}
                    <div>
                        <label className="text-sm font-medium">Start Date *</label>
                        <input
                            type="date"
                            name="start_date"
                            value={formData.start_date}
                            onChange={handleChange}
                            onBlur={(e) => validateField(e.target.name, e.target.value)}
                            className={`border p-2 w-full rounded ${errors.start_date ? "border-red-500" : ""}`}
                        />
                        {errors.start_date && <p className="text-red-500 text-xs">{errors.start_date}</p>}
                    </div>

                    {/* End Date */}
                    <div>
                        <label className="text-sm font-medium">End Date *</label>
                        <input
                            type="date"
                            name="end_date"
                            value={formData.end_date}
                            onChange={handleChange}
                            onBlur={(e) => validateField(e.target.name, e.target.value)}
                            className={`border p-2 w-full rounded ${errors.end_date ? "border-red-500" : ""}`}
                        />
                        {errors.end_date && <p className="text-red-500 text-xs">{errors.end_date}</p>}
                    </div>

                    {/* Project Value */}
                    <div>
                        <label className="text-sm font-medium">Project Value *</label>
                        <input
                            type="number"
                            name="project_value"
                            value={formData.project_value}
                            onChange={handleChange}
                            onBlur={(e) => validateField(e.target.name, e.target.value)}
                            className={`border p-2 w-full rounded ${errors.project_value ? "border-red-500" : ""}`}
                        />
                        {errors.project_value && <p className="text-red-500 text-xs">{errors.project_value}</p>}
                    </div>

                    {/* Approved Budget */}
                    <div>
                        <label className="text-sm font-medium">Approved Budget *</label>
                        <input
                            type="number"
                            name="approved_budget"
                            value={formData.approved_budget}
                            onChange={handleChange}
                            onBlur={(e) => validateField(e.target.name, e.target.value)}
                            className={`border p-2 w-full rounded ${errors.approved_budget ? "border-red-500" : ""}`}
                        />
                        {errors.approved_budget && <p className="text-red-500 text-xs">{errors.approved_budget}</p>}
                    </div>

                    {/* Project Manager */}
                    <div>
                        <label className="text-sm font-medium">Project Manager</label>
                        <select
                            name="project_manager_id"
                            value={formData.project_manager_id}
                            onChange={handleChange}
                            className="border p-2 w-full rounded"
                        >
                            <option value="">Select Manager</option>
                            {managers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                        </select>
                    </div>

                    {/* Status */}
                    <div>
                        <label className="text-sm font-medium">Status</label>
                        <select
                            name="status"
                            value={formData.status}
                            onChange={handleChange}
                            className="border p-2 w-full rounded"
                        >
                            {statuses.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                        </select>
                    </div>

                    {/* Description */}
                    <div className="col-span-2">
                        <label className="text-sm font-medium">Description</label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            rows={3}
                            className="border p-2 w-full rounded"
                        />
                    </div>
                </div>

                {/* Buttons */}
                <div className="flex justify-end gap-2 mt-6">
                    <button onClick={() => navigate("/projects")} className="px-4 py-2 bg-gray-300 rounded">Cancel</button>
                    <button onClick={handleUpdate} className="px-4 py-2 bg-blue-600 text-white rounded">Update Project</button>
                </div>
            </div>
        </div>
    );
}